import React, { useEffect, useState } from "react";
import "./Profile.scss";
import {
  getUserProfileData,
  updateUserProfile,
  updateCompanyProfile,
  getPlanData,
} from "../../services/api/AuthApi";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  getClientCompanyData,
  updateClientCompanyData,
} from "../../services/api/ProfileApi";
import { useToast } from "../../context/ToastContext";
import { useLoading } from "../../context/LoadingContext";
import PlanSelectionModal from "../../components/PlanSelectionModal/PlanSelectionModal";

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const { company, setCompany } = useAuth();
  const navigate = useNavigate  ();

  const [editableUser, setEditableUser] = useState({});
  const [editableCompany, setEditableCompany] = useState({});
  const [clientCompany, setClientCompany] = useState(null);
  const [editableClientCompany, setEditableClientCompany] = useState({});
  const [isEditingClientCompany, setIsEditingClientCompany] = useState(false);
  const [planData, setPlanData] = useState(null);
  const { showToast } = useToast();
  const { showLoading, hideLoading } = useLoading();

  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);

  const handleSelectPlan = (selectedPlan, companyId, paymentData) => {
    console.log("Selected Plan:");
    console.log("Company ID:");
    console.log("Payment Data:");
    // Payment is handled in PlanSelectionModal
    // This callback is called after successful payment
  };

  const fetchData = async () => {
    try {
      showLoading("Loading profile...");
      const response = await getUserProfileData();
      setProfile(response.data);
      setEditableUser(response.data);
      setEditableCompany(company || {});
      hideLoading();
    } catch (err) {
      console.error("Error fetching profile:", err);
      hideLoading();
    }
  };

  const fetchPlanData = async () => {
    try {
      if (company?.planId) {
        //showLoading("Loading plan data...");
        const response = await getPlanData(company.planId);
        setPlanData(response.data);
        //hideLoading();
      }
    } catch (err) {
      console.error("Error fetching plan data:", err);
      //hideLoading();
    }
  };

  useEffect(() => {
    fetchData();
  }, [company]);

  useEffect(() => {
    if (
      (profile?.role === "admin" || profile?.role === "superAdmin") &&
      company
    ) {
      fetchPlanData();
    }
  }, [profile, company]);

  useEffect(() => {
    if (profile?.role === "customer") {
      const fetchClientData = async () => {
        try {
          showLoading("Loading client data...");
          const res = await getClientCompanyData();
          if (Array.isArray(res.data) && res.data.length > 0) {
            setClientCompany(res.data[0]);
            setEditableClientCompany(res.data[0]);
          }
          hideLoading();
        } catch (err) {
          console.error("Failed to fetch client company data:", err);
          hideLoading();
        }
      };

      fetchClientData();
    }
  }, [profile]);

  const handleClientCompanyChange = (e) => {
    const { name, value } = e.target;
    setEditableClientCompany((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClientCompanySave = async () => {
    try {
      showLoading("Updating client data...");
      const {
        _id,
        salutation,
        name,
        company,
        phno,
        alternateContact,
        email,
        gstin,
        addressLine1,
        addressLine2,
        city,
        state,
        zipcode,
        country,
      } = editableClientCompany;

      const payload = {
        salutation,
        name,
        company,
        phno,
        alternateContact,
        email,
        gstin,
        addressLine1,
        addressLine2,
        city,
        state,
        zipcode,
        country,
      };

      await updateClientCompanyData(_id, payload);

      hideLoading();
      showToast("Client company data updated!", "success");
      setIsEditingClientCompany(false);
      setClientCompany((prev) => ({ ...prev, ...payload }));
    } catch (err) {
      console.error("Error updating client data:", err);
      hideLoading();
      showToast("Failed to update client data.", "error");
    }
  };

  const handleUserChange = (e) => {
    const { name, value } = e.target;
    setEditableUser((prev) => ({ ...prev, [name]: value }));
  };

  const handleCompanyChange = (e) => {
    const { name, value } = e.target;
    setEditableCompany((prev) => ({ ...prev, [name]: value }));
  };

  const handleUserSave = async () => {
    try {
      showLoading("Updating user profile...");
      await updateUserProfile(editableUser._id, {
        name: editableUser.name,
        // phno: editableUser.phno,
        // email: editableUser.email,
      });
      hideLoading();
      showToast("User profile updated!", "success");
      setIsEditingUser(false);
      fetchData();
    } catch (err) {
      hideLoading();
      showToast("Failed to update user.", "error");
    }
  };

  const handleCompanySave = async () => {
    try {
      showLoading("Updating company profile...");
      const updated = await updateCompanyProfile(
        editableCompany._id,
        editableCompany
      );
      hideLoading();
      showToast("Company profile updated!", "success");
      setIsEditingCompany(false);
      setCompany((prev) => ({ ...prev, ...editableCompany }));
      fetchData();
    } catch (err) {
      hideLoading();
      showToast("Failed to update company.", "error");
    }
  };

  // Handlers for Enter key press
  const handleUserKeyDown = (event) => {
    if (event.key === "Enter") {
      handleUserSave();
    }
  };

  const handleCompanyKeyDown = (event) => {
    if (event.key === "Enter") {
      handleCompanySave();
    }
  };

  const handleClientCompanyKeyDown = (event) => {
    if (event.key === "Enter") {
      handleClientCompanySave();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (!profile) return <div className="profile-page">Loading...</div>;

  return (
    <>
      <div className="profile-page">
        {/* <h2>My Profile</h2> */}
        {/* ADDED 'user-info-card' class below */}
        <div className="profile-card user-info-card">
          <div className="card-header">
            <h3>User Information</h3>
            {profile.role !== "admin" && profile.role !== "superAdmin" && (
              <button
                onClick={() => setIsEditingUser(!isEditingUser)}
                className="edit-btn"
              >
                {isEditingUser ? "Cancel" : "Edit"}
              </button>
            )}
          </div>

          <div className="settings-section">
            <h4 className="section-title">Personal Details</h4>
            <div className="form-grid">
              <div className="form-group">
                <label>Name</label>
                {isEditingUser ? (
                  <input
                    name="name"
                    value={editableUser.name}
                    onChange={handleUserChange}
                    placeholder="Enter your name"
                    autoFocus
                    onKeyDown={handleUserKeyDown}
                  />
                ) : (
                  <div className="field-value">{profile.name}</div>
                )}
              </div>
              <div className="form-group">
                <label>Role</label>
                <div className="field-value">{profile.role}</div>
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h4 className="section-title">Contact Information</h4>
            <div className="form-grid">
              <div className="form-group">
                <label>Email Address</label>
                {/* {isEditingUser && profile.role !== "admin" && profile.role !== "moderator" ? (
                  <input
                    name="email"
                    type="email"
                    value={editableUser.email}
                    onChange={handleUserChange}
                    placeholder="Enter email address"
                    onKeyDown={handleUserKeyDown}
                  />
                ) : (
                  <div className="field-value">{profile.email}</div>
                )} */}
                <div className="field-value">{profile.email}</div>
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                {/* {isEditingUser && profile.role !== "admin" && profile.role !== "moderator" ? (
                  <input
                    name="phno"
                    value={editableUser.phno}
                    onChange={handleUserChange}
                    placeholder="Enter phone number"
                    onKeyDown={handleUserKeyDown}
                  />
                ) : (
                  <div className="field-value">{profile.phno}</div>
                )} */}
                <div className="field-value">{profile.phno}</div>
              </div>
            </div>
          </div>

          {isEditingUser && (
            <div className="action-buttons">
              <button className="save-btn" onClick={handleUserSave}>
                Save Changes
              </button>
            </div>
          )}
        </div>

        {(profile.role === "admin" || profile.role === "superAdmin") &&
          company && (
            <>
              {/* ADDED 'company-info-card' class below */}
              <div className="profile-card company-info-card">
                <div className="card-header">
                  <h3>Company Information</h3>
                  <button
                    onClick={() => setIsEditingCompany(!isEditingCompany)}
                    className="edit-btn"
                  >
                    {isEditingCompany ? "Cancel" : "Edit"}
                  </button>
                </div>

                <div className="settings-section">
                  <h4 className="section-title">Basic Information</h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Company Name</label>
                      <div className="field-value">{company.companyName}</div>
                    </div>
                    <div className="form-group">
                      <label>Website</label>
                      {isEditingCompany ? (
                        <input
                          name="website"
                          value={editableCompany.website || ""}
                          onChange={handleCompanyChange}
                          placeholder="Enter website URL"
                          autoFocus
                          onKeyDown={handleCompanyKeyDown}
                        />
                      ) : (
                        <div className="field-value">{company.website}</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="settings-section">
                  <h4 className="section-title">Contact Details</h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Phone Number</label>
                      <div className="field-value">{company.phno}</div>
                    </div>
                    <div className="form-group">
                      <label>Email Address</label>
                      <div className="field-value">{company.email}</div>
                    </div>
                  </div>
                </div>

                <div className="settings-section">
                  <h4 className="section-title">Tax & Legal Information</h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>GST Number</label>
                      {isEditingCompany ? (
                        <input
                          name="gstNo"
                          value={editableCompany.gstNo || ""}
                          onChange={handleCompanyChange}
                          placeholder="Enter GST number"
                          onKeyDown={handleCompanyKeyDown}
                        />
                      ) : (
                        <div className="field-value">{company.gstNo}</div>
                      )}
                    </div>
                    {/* <div className="form-group">
                    <label>VAT Number</label>
                    {isEditingCompany ? (
                      <input
                        name="vatNo"
                        value={editableCompany.vatNo || ""}
                        onChange={handleCompanyChange}
                        placeholder="Enter VAT number"
                        onKeyDown={handleCompanyKeyDown}
                      />
                    ) : (
                      <div className="field-value">{company.vatNo}</div>
                    )}
                  </div> */}
                  </div>
                </div>

                <div className="settings-section">
                  <h4 className="section-title">Address Information</h4>
                  <div className="form-group">
                    <label>Company Address</label>
                    {isEditingCompany ? (
                      <textarea
                        name="address"
                        value={editableCompany.address || ""}
                        onChange={handleCompanyChange}
                        placeholder="Enter complete address"
                        rows="3"
                        onKeyDown={handleCompanyKeyDown}
                      />
                    ) : (
                      <div className="field-value">{company.address}</div>
                    )}
                  </div>
                </div>

                {isEditingCompany && (
                  <div className="action-buttons">
                    <button className="save-btn" onClick={handleCompanySave}>
                      Save Changes
                    </button>
                  </div>
                )}
              </div>

              {planData && (
                <div className="profile-card plan-card">
                  <div className="card-header">
                    <h3>Plan Information</h3>
                    {company?.type !== "ent" && (
                      <button
                        className="edit-btn"
                        onClick={() => {
                          if (company?._id) {
                            setIsPlanModalOpen(true);
                          } else {
                            console.error("Company ID not found");
                          }
                        }}
                      >
                        {company?.subscription?.paymentStatus ===
                          "Free Trial" ||
                        company?.subscription?.paymentStatus
                          ?.toLowerCase()
                          .includes("trial")
                          ? "Upgrade to Continue"
                          : "Upgrade Now"}
                      </button>
                    )}
                  </div>

                  <div className="settings-section">
                    <h4 className="section-title">Current Plan</h4>
                    <div className="plan-grid">
                      <div className="form-group">
                        <label>Plan Name</label>
                        <div className="field-value plan-name">
                          {planData.name}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Price</label>
                        <div className="field-value">
                          ₹{(company?.subscription?.amountPaid || (planData.price * 1.18))?.toLocaleString()}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Case Limit</label>
                        <div className="field-value">{planData.caseLimit}</div>
                      </div>
                      <div className="form-group">
                        <label>User Limit</label>
                        <div className="field-value">{planData.userLimit}</div>
                      </div>
                      <div className="form-group">
                        <label>Period Start</label>
                        <div className="field-value">
                          {formatDate(company.subscription.currentPeriodStart)}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Period End</label>
                        <div className="field-value">
                          {formatDate(company.subscription.currentPeriodEnd)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        {profile.role === "customer" && clientCompany && (
          <div className="profile-card">
            <div className="card-header">
              <h3>Client Company Information</h3>
              <button
                onClick={() =>
                  setIsEditingClientCompany(!isEditingClientCompany)
                }
                className="edit-btn"
              >
                {isEditingClientCompany ? "Cancel" : "Edit"}
              </button>
            </div>

            <div className="settings-section">
              <h4 className="section-title">Personal Details</h4>
              <div className="form-grid">
                {/* <div className="form-group">
                  <label>Salutation</label>
                  {isEditingClientCompany ? (
                    <select
                      name="salutation"
                      value={editableClientCompany.salutation || ""}
                      onChange={handleClientCompanyChange}
                    >
                      <option value="">Select</option>
                      <option value="Mr.">Mr.</option>
                      <option value="Ms.">Ms.</option>
                      <option value="Mrs.">Mrs.</option>
                      <option value="Dr.">Dr.</option>
                    </select>
                  ) : (
                    <div className="field-value">
                      {clientCompany.salutation}
                    </div>
                  )}
                </div> */}
                <div className="form-group">
                  <label>Full Name</label>
                  {isEditingClientCompany ? (
                    <input
                      name="name"
                      value={editableClientCompany.name || ""}
                      onChange={handleClientCompanyChange}
                      placeholder="Enter full name"
                      onKeyDown={handleClientCompanyKeyDown}
                    />
                  ) : (
                    <div className="field-value">{clientCompany.name}</div>
                  )}
                </div>
                <div className="form-group">
                  <label>Company Name</label>
                  {isEditingClientCompany ? (
                    <input
                      name="company"
                      value={editableClientCompany.company || ""}
                      onChange={handleClientCompanyChange}
                      placeholder="Enter company name"
                      onKeyDown={handleClientCompanyKeyDown}
                    />
                  ) : (
                    <div className="field-value">{clientCompany.company}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="settings-section">
              <h4 className="section-title">Contact Information</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label>Primary Phone</label>
                  {isEditingClientCompany ? (
                    <input
                      name="phno"
                      value={editableClientCompany.phno || ""}
                      onChange={handleClientCompanyChange}
                      placeholder="Enter primary phone"
                      onKeyDown={handleClientCompanyKeyDown}
                    />
                  ) : (
                    <div className="field-value">{clientCompany.phno}</div>
                  )}
                </div>
                <div className="form-group">
                  <label>Alternate Contact</label>
                  {isEditingClientCompany ? (
                    <input
                      name="alternateContact"
                      value={editableClientCompany.alternateContact || ""}
                      onChange={handleClientCompanyChange}
                      placeholder="Enter alternate contact"
                      onKeyDown={handleClientCompanyKeyDown}
                    />
                  ) : (
                    <div className="field-value">
                      {clientCompany.alternateContact}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Email Address</label>
                  {isEditingClientCompany ? (
                    <input
                      name="email"
                      type="email"
                      value={editableClientCompany.email || ""}
                      onChange={handleClientCompanyChange}
                      placeholder="Enter email address"
                      onKeyDown={handleClientCompanyKeyDown}
                    />
                  ) : (
                    <div className="field-value">{clientCompany.email}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="settings-section">
              <h4 className="section-title">Tax Information</h4>
              <div className="form-group">
                <label>GSTIN Number</label>
                {isEditingClientCompany ? (
                  <input
                    name="gstin"
                    value={editableClientCompany.gstin || ""}
                    onChange={handleClientCompanyChange}
                    placeholder="Enter GSTIN number"
                    onKeyDown={handleClientCompanyKeyDown}
                  />
                ) : (
                  <div className="field-value">{clientCompany.gstin}</div>
                )}
              </div>
            </div>

            <div className="settings-section">
              <h4 className="section-title">Address Details</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label>Address Line 1</label>
                  {isEditingClientCompany ? (
                    <input
                      name="addressLine1"
                      value={editableClientCompany.addressLine1 || ""}
                      onChange={handleClientCompanyChange}
                      placeholder="Enter address line 1"
                      onKeyDown={handleClientCompanyKeyDown}
                    />
                  ) : (
                    <div className="field-value">
                      {clientCompany.addressLine1}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Address Line 2</label>
                  {isEditingClientCompany ? (
                    <input
                      name="addressLine2"
                      value={editableClientCompany.addressLine2 || ""}
                      onChange={handleClientCompanyChange}
                      placeholder="Enter address line 2"
                      onKeyDown={handleClientCompanyKeyDown}
                    />
                  ) : (
                    <div className="field-value">
                      {clientCompany.addressLine2}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>City</label>
                  {isEditingClientCompany ? (
                    <input
                      name="city"
                      value={editableClientCompany.city || ""}
                      onChange={handleClientCompanyChange}
                      placeholder="Enter city"
                      onKeyDown={handleClientCompanyKeyDown}
                    />
                  ) : (
                    <div className="field-value">{clientCompany.city}</div>
                  )}
                </div>
                <div className="form-group">
                  <label>State</label>
                  {isEditingClientCompany ? (
                    <input
                      name="state"
                      value={editableClientCompany.state || ""}
                      onChange={handleClientCompanyChange}
                      placeholder="Enter state"
                      onKeyDown={handleClientCompanyKeyDown}
                    />
                  ) : (
                    <div className="field-value">{clientCompany.state}</div>
                  )}
                </div>
                <div className="form-group">
                  <label>Zipcode</label>
                  {isEditingClientCompany ? (
                    <input
                      name="zipcode"
                      value={editableClientCompany.zipcode || ""}
                      onChange={handleClientCompanyChange}
                      placeholder="Enter zipcode"
                      onKeyDown={handleClientCompanyKeyDown}
                    />
                  ) : (
                    <div className="field-value">{clientCompany.zipcode}</div>
                  )}
                </div>
                <div className="form-group">
                  <label>Country</label>
                  {isEditingClientCompany ? (
                    <input
                      name="country"
                      value={editableClientCompany.country || ""}
                      onChange={handleClientCompanyChange}
                      placeholder="Enter country"
                      onKeyDown={handleClientCompanyKeyDown}
                    />
                  ) : (
                    <div className="field-value">{clientCompany.country}</div>
                  )}
                </div>
              </div>
            </div>

            {isEditingClientCompany && (
              <div className="action-buttons">
                <button className="save-btn" onClick={handleClientCompanySave}>
                  Save Changes
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <PlanSelectionModal
        isOpen={isPlanModalOpen}
        companyId={company?._id}
        onClose={() => setIsPlanModalOpen(false)}
        onSelectPlan={handleSelectPlan}
      />
    </>
  );
};

export default Profile;